import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service, { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { pluralize } from 'ember-inflector';
import { Promise as Promise$1 } from 'rsvp';

var _class, _descriptor, _descriptor2, _descriptor3;

/** Main model list used for connected-user presence bookkeeping */
const MODEL_NAMES = ['contact', 'event', 'expense', 'invoice', 'payment', 'product', 'project', 'workhour'];

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — realtime socket handle + connected-user presence
 *              bookkeeping and safe record reload
 *   extends:   Service (ember, base class)
 *   template:  none
 *   key:       updateUsersStatus, updateUserStatusForModelData,
 *              updateCurrentUserForRecord, reloadRecord
 *   uses:      store, log services; ember-inflector (pluralize); rsvp (Promise)
 *   siblings:  controllers/application.js (socket façade getter/setter over
 *              this service's @tracked `socket`; startSocket/_handleSocketEvent/
 *              updateSocketRecord/deleteSocketRecord/checkStopSocket/
 *              tmpSaveLastSavedRecord/_reloadModelIndex stay on the controller —
 *              see gotchas)
 *   tests:     socket-sync-test.js
 *   gotchas:   conservative extraction — the controller-side handlers above were
 *              deliberately NOT moved here because they depend on
 *              controller-context state (content, lastSavedRecord, modalComponent,
 *              activeCall) and dynamic `set(this, 'applicationController.
 *              indexLoaded.*')` paths that must keep their exact controller-
 *              relative form; reloadRecord only reloads when the record's
 *              currentState is not in-flight/loading/deleted (see the excluded
 *              stateName list) to avoid a redundant/racy reload
 *
 * SocketSync service
 * Owns the realtime socket handle and the cleanly-movable socket sync helpers
 * (connected-user presence bookkeeping + safe record reload). Extracted from the
 * application god-controller (maintainability 4.2, group E), behavior unchanged.
 *
 * NOTE (conservative extraction): startSocket/_handleSocketEvent/updateSocketRecord/
 * deleteSocketRecord/checkStopSocket/tmpSaveLastSavedRecord/_reloadModelIndex stay on
 * the controller — they depend on controller-context state (`content`, `lastSavedRecord`,
 * `modalComponent`, `activeCall`) and on dynamic `set(this, 'applicationController.
 * indexLoaded.*')` paths that must keep their exact controller-relative form.
 */
let SocketSyncService = (_class = class SocketSyncService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "log", _descriptor2, this);
    /**
     * Socket property
     * When a socket is connected this property is set with
     * io.connect() result otherwise is false. Read cross-repo via the
     * `applicationController.socket` façade getter/setter.
     *
     * @property socket
     * @type {Object}
     */
    _initializerDefineProperty(this, "socket", _descriptor3, this);
  }
  /**
   * Aggiorna lo stato degli utenti connessi per ogni modello
   * @param {Object} data - Dati dello stato utenti dal socket
   */
  updateUsersStatus(data) {
    if (!data) {
      return false;
    }
    MODEL_NAMES.forEach(model => {
      const modelPlural = pluralize(model);
      if (data[modelPlural]?.data) {
        const items = this.store.peekAll(model);

        // Reset current_users per tutti i record
        items.forEach(record => {
          if (record) {
            record.set('current_users', []);
          }
        });
        this.updateUserStatusForModelData(data[modelPlural].data, model);
      }
    });
  }

  /**
   * Aggiorna lo stato utente per i dati di un modello specifico
   * @private
   * @param {Array} modelData - Array di dati del modello
   * @param {string} model - Nome del modello
   */
  updateUserStatusForModelData(modelData, model) {
    const currentSocketId = this.socket?.socket?.id;
    modelData.forEach(item => {
      if (currentSocketId !== item.socket_id) {
        const record = this.store.peekRecord(model, item.id);
        if (record) {
          this.updateCurrentUserForRecord(record, item.user_id);
        }
      }
    });
  }

  /**
   * Aggiorna la lista current_users di un record con l'utente specificato
   * @private
   * @param {Object} record - Record Ember Data
   * @param {string} userId - ID dell'utente
   */
  updateCurrentUserForRecord(record, userId) {
    const user = this.store.peekRecord('contact', userId);
    const canPush = () => record && !record.isDestroyed && !record.isDestroying;
    if (user) {
      if (canPush()) record.current_users.push(user);
    } else {
      this.store.findRecord('contact', userId).then(foundUser => {
        // the record may have been unloaded while the contact loaded
        if (canPush()) record.current_users.push(foundUser);
      }).catch(e => this.log.warn('[updateCurrentUserForRecord] findRecord contact failed:', userId, e));
    }
  }

  /**
   * Reload a record only when it is in a safe (non in-flight) state.
   * @param {Object} record - Record Ember Data
   * @returns {Promise}
   */
  reloadRecord(record) {
    const {
      currentState: {
        stateName,
        isSaving
      }
    } = record;
    if (!isSaving && !['root.loaded.updated.inFlight', 'root.loading', 'root.deleted.saved', 'root.deleted.inFlight'].includes(stateName)) {
      return record.reload();
    }
    return Promise$1.resolve(record);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "socket", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _class);

export { SocketSyncService as default };
//# sourceMappingURL=socket-sync.js.map
